import findStep from "./findStep";
import useBotControlStore from "../../../storeModules/useBotControlStore";
import useUserStore from "../../../storeModules/useUserStore";
import { validateCoupon, getProposta } from "../../../api/requests";
import {
  setLoadingMessage,
  removeLoadingMessage,
} from "../../../utils/setLoadingMessage";
import sleepAwait from "../../../utils/sleepAwait";
import useInitialConfigStore from "../../../storeModules/useInitialConfigStore";
import addMoneyMask from "../../../utils/addMoneyMask";
import { metodosPagamentoEnum, couponErrors } from "../../../utils/constants";
import hasUrlCoupon from "../../../utils/hasUrlCoupon";
import { persistCupomStore } from "../../../utils/resumeStorage";
import {
  tagSemCupom,
  tagAplicarCupom,
  tagValidarCupom,
  tagAplicarNovoCupom,
  tagContinuarSemCupom,
} from "../../../utils/sendTag";
import noConnectionHandler from "../../../utils/noConnectionHandler";
import usePaymentStore from "../../../storeModules/usePaymentStore";
import useInsuranceStore from "../../../storeModules/useInsuranceStore";

const handleGetProposta = async (userCupom) => {
  await noConnectionHandler().then(() => {
    getProposta(userCupom);
  });
};

const handleValidateCoupon = async () => {
  const { fakeLoad } = useInitialConfigStore.getState();
  const { userCupom } = useUserStore.getState();

  const paymentState = usePaymentStore.getState();

  if (userCupom) {
    persistCupomStore();
    await noConnectionHandler().then(async () => {
      setLoadingMessage();
      await sleepAwait(fakeLoad);

      validateCoupon()
        .then(async (response) => {
          const cupomData = response.data.value;
          const userStoreData = useUserStore.getState();
          const { firstParcel: valorComCampanha } = usePaymentStore.getState();

          const cupomMaisVantajoso =
            cupomData.valorDescontado < valorComCampanha;

          usePaymentStore.setState({
            ...paymentState,
            couponFirstParcel: cupomMaisVantajoso
              ? cupomData.valorDescontado
              : valorComCampanha,
          });

          useUserStore.setState({
            ...userStoreData,
            cupomData: { ...cupomData, cupomMaisVantajoso },
          });
          persistCupomStore();

          useBotControlStore.setState({
            currentStep: findStep("cupom_aplicado").index,
          });

          tagValidarCupom(true, userCupom);
        })
        .catch((error) => {
          const errorMessages = error.response.data.validationErrors
            .flat()
            .map((error) => {
              return error.code;
            });

          useUserStore.setState({
            userCupom: "",
          });

          useBotControlStore.setState({
            couponErrorMessages: errorMessages,
          });

          useBotControlStore.setState({
            currentStep: findStep("erro_cupom").index,
          });

          tagValidarCupom(false, userCupom);
        })
        .finally(() => {
          removeLoadingMessage();
        });
    });
  }
};

const clearStoreCoupon = () => {
  const { userStoreData } = useUserStore.getState();

  useUserStore.setState({
    ...userStoreData,
    userCupom: "",
    cupomData: {
      valorDescontado: 0,
      valorTotal: 0,
      valorDesconto: 0,
      tipoDesconto: 0,
      cupomId: 0,
      formasPagamentosValidas: [],
    },
  });
};

const cupomSteps = [
  {
    name: "pergunta_cupom",
    sender: "bot",
    text: () => {
      return `Você tem um <b>cupom de desconto</b> para aplicar?`;
    },
    options: [
      {
        text: "SIM, QUERO APLICAR",
        action: async () => {
          tagAplicarCupom();
          useBotControlStore.setState({
            currentStep: findStep("informa_codigo_cupom").index,
          });
        },
      },
      {
        text: "NÃO TENHO CUPOM",
        action: async () => {
          tagSemCupom("nao_tenho_cupom");
          clearStoreCoupon();
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
  {
    name: "informa_codigo_cupom",
    sender: "bot",
    inputType: "userCupom",
    text: () => "Digite seu cupom de desconto.",
    action: () => {
      handleValidateCoupon();
    },
    options: [
      {
        text: "NÃO TENHO CUPOM",
        action: async () => {
          tagSemCupom("nao_tenho_cupom");
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
  {
    name: "erro_cupom",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const { couponErrorMessages } = useBotControlStore.getState();

      const couponErrorTranslation = couponErrorMessages.map((error) => {
        return couponErrors[error] || "O cupom não pode ser aplicado.";
      });

      const couponErrorMessage = couponErrorTranslation
        .map((message) => `• ${message}<br>`)
        .join(" ");

      return `
              Cupom inválido.
              <br>
              <ul class="coupon-list-error-message">
                ${couponErrorMessage}
              </ul>
            `;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("erro_cupom_opcoes").index,
      });
    },
  },
  {
    name: "erro_cupom_opcoes",
    sender: "bot",
    isWaitingForFetch: true,
    text: "O que você gostaria de fazer?",
    options: [
      {
        text: "APLICAR UM NOVO CUPOM",
        action: async () => {
          tagAplicarNovoCupom();
          useBotControlStore.setState({
            currentStep: findStep("tentar_cupom_novamente").index,
          });
        },
      },
      {
        text: "CONTINUAR SEM CUPOM",
        action: async () => {
          tagContinuarSemCupom();
          clearStoreCoupon();
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
  {
    name: "tentar_cupom_novamente",
    sender: "bot",
    inputType: "userCupom",
    text: () => {
      return `Adicione um <b>novo cupom de desconto</b>.`;
    },
    action: () => {
      handleValidateCoupon();
    },
    options: [
      {
        text: "NÃO, CONTINUAR SEM CUPOM",
        action: async () => {
          tagContinuarSemCupom();
          clearStoreCoupon();
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
  {
    name: "cupom_aplicado",
    sender: "bot",
    isWaitingForFetch: true,
    text: () => {
      const { cupomData } = useUserStore.getState();

      if (!cupomData.cupomMaisVantajoso) {
        return `Você já possui um desconto especial na primeira parcela mais vantajoso que este cupom, por isso vamos manter o desconto atual.`;
      }

      const discountMessage =
        cupomData.tipoDesconto === 1
          ? `${addMoneyMask(cupomData.valorDesconto)}`
          : `${cupomData.valorDesconto}%`;

      return `Cupom aplicado com sucesso! Você ganhou <b>${discountMessage} de desconto</b> na primeira parcela.`;
    },
    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("novo_valor_com_cupom").index,
      });
    },
  },
  {
    name: "novo_valor_com_cupom",
    sender: "bot",
    text: () => {
      const { couponFirstParcel } = usePaymentStore.getState();
      const { valorPremio, isInsuranceHired } = useInsuranceStore.getState();

      return `E o novo valor da primeira parcela é <b>${addMoneyMask(
        isInsuranceHired ? valorPremio + couponFirstParcel : couponFirstParcel,
      )}</b>`;
    },

    action: () => {
      useBotControlStore.setState({
        currentStep: findStep("mensagem_pagamento_cupom").index,
      });
    },
  },
  {
    name: "mensagem_pagamento_cupom",
    sender: "bot",
    text: () => {
      const { cupomData } = useUserStore.getState();
      const paymentMethods = cupomData.formasPagamentosValidas.map(
        (paymentMethod) => {
          return `<b>${metodosPagamentoEnum[paymentMethod]}</b>`;
        },
      );
      return cupomData.formasPagamentosValidas.length < 3
        ? `Lembre-se: os cupons são válidos apenas para pagamentos via ${paymentMethods.join(
            ", ",
          )}`
        : null;
    },
    options: [
      {
        text: "CONFIRMAR",
        action: async () => {
          const { userCupom, cupomData } = useUserStore.getState();
          const { couponFirstParcel } = usePaymentStore.getState();
          const cupomVaiSerAplicado =
            userCupom && cupomData.cupomId && cupomData.cupomMaisVantajoso;

          noConnectionHandler().then(() => {
            handleGetProposta(cupomVaiSerAplicado ? userCupom : undefined);
          });

          window.plano_selecionado.valor_primeira_parcela = couponFirstParcel;
        },
      },
      {
        text: "NÃO QUERO USAR O CUPOM",
        action: async () => {
          tagSemCupom("nao_quero_usar_cupom");
          clearStoreCoupon();
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
  {
    name: "cupom_url_detectado",
    sender: "bot",
    text: () => {
      return `Você tem um cupom disponível.`;
    },
    action: () => {
      const userStoreData = useUserStore.getState();

      useUserStore.setState({
        ...userStoreData,
        userCupom: !!hasUrlCoupon() ? hasUrlCoupon() : "",
      });

      useBotControlStore.setState({
        currentStep: findStep("aplica_cupom_url").index,
      });
    },
  },
  {
    name: "aplica_cupom_url",
    sender: "bot",
    text: () => {
      const urlCoupon = hasUrlCoupon();
      return `Deseja aplicar o cupom: <b>${urlCoupon}</b>?`;
    },
    options: [
      {
        text: "SIM, QUERO APLICAR",
        action: async () => {
          tagAplicarCupom();
          handleValidateCoupon();
        },
      },
      {
        text: "NÃO QUERO USAR O CUPOM",
        action: async () => {
          tagSemCupom("nao_quero_usar_cupom");
          clearStoreCoupon();
          noConnectionHandler().then(() => {
            handleGetProposta();
          });
        },
      },
    ],
  },
];

export default cupomSteps;
